import qs from 'qs';

const PAYMENT_RETURN_PARAM_KEYS = [
  'onteexBackFromStripe',
  'onteexBackFromKlarna',
  'onteexStatus',
  'onteexBookingId',
  'onteexOrderId',
  'onteexClientReferenceId'
];

/**
 * Extract callback parameters from parent-url query string.
 * This mirrors existing callback parsing behavior without Stripe dependencies.
 */
export const extractParentParams = queryString => {
  const params = qs.parse(queryString, { ignoreQueryPrefix: true });
  const parentUrl = params['parent-url'];
  const parentUrlQueryString = parentUrl?.indexOf('?') >= 0 ? parentUrl.substring(parentUrl.indexOf('?')) : null;
  return qs.parse(parentUrlQueryString, { ignoreQueryPrefix: true });
};

/**
 * Payment return params from parent-url embed and/or top-level query (standalone widget / redirect bridge).
 */
export const extractWidgetPaymentReturnParams = queryString => {
  const parentParams = extractParentParams(queryString) || {};
  const topLevelParams = qs.parse(queryString, { ignoreQueryPrefix: true });
  return { ...parentParams, ...topLevelParams };
};

const parsePositiveInt = value => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
};

/**
 * Klarna/Stripe browser return after customer cancelled payment in provider UI.
 */
export const isKlarnaPaymentCancelReturn = params => {
  if (!params || typeof params !== 'object') {
    return false;
  }
  const status = typeof params.onteexStatus === 'string' ? params.onteexStatus.trim().toLowerCase() : '';
  const bookingId = parsePositiveInt(params.onteexBookingId);
  return status === 'cancel' && bookingId !== null;
};

/**
 * Stripe success return embedded via parent-url (Klarna success uses /widget/booking/confirmed).
 */
export const isPaymentSuccessReturn = params => {
  if (!params || typeof params !== 'object') {
    return false;
  }
  const status = typeof params.onteexStatus === 'string' ? params.onteexStatus.trim().toLowerCase() : '';
  return status === 'success' || params.onteexBackFromStripe === '1' || params.onteexBackFromStripe === 1;
};

/**
 * Removes payment return params from the current URL without navigation.
 */
export const stripPaymentReturnParamsFromBrowserUrl = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const url = new URL(window.location.href);
    PAYMENT_RETURN_PARAM_KEYS.forEach(key => {
      url.searchParams.delete(key);
    });

    const parentUrlRaw = url.searchParams.get('parent-url');
    if (parentUrlRaw) {
      try {
        const parentUrl = new URL(parentUrlRaw);
        PAYMENT_RETURN_PARAM_KEYS.forEach(key => {
          parentUrl.searchParams.delete(key);
        });
        url.searchParams.set('parent-url', parentUrl.toString());
      } catch {
        // keep parent-url unchanged if not a valid absolute URL
      }
    }

    window.history.replaceState({}, '', url.toString());
  } catch {
    // ignore invalid URLs
  }
};

/**
 * Build callback URL by clearing OnTee payment state fields from parent query.
 * This URL is sent to the backend processOrder endpoint.
 */
export const createCallbackUrl = url => {
  let fullParentUrl = null;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    fullParentUrl = parsedUrl.searchParams.get('parent-url');
  } catch (_error) {
    const params = qs.parse(url, { ignoreQueryPrefix: true });
    fullParentUrl = params['parent-url'];
  }

  if (!fullParentUrl) {
    return url;
  }

  const parentUrl =
    fullParentUrl?.indexOf('?') >= 0 ? fullParentUrl.substring(0, fullParentUrl.indexOf('?')) : fullParentUrl;
  const parentUrlQueryString =
    fullParentUrl?.indexOf('?') >= 0 ? fullParentUrl.substring(fullParentUrl.indexOf('?')) : null;
  const parentParams = qs.parse(parentUrlQueryString, { ignoreQueryPrefix: true });

  delete parentParams.onteexBackFromStripe;
  delete parentParams.onteexBackFromKlarna;
  delete parentParams.onteexStatus;
  delete parentParams.onteexBookingId;
  delete parentParams.onteexOrderId;
  delete parentParams.onteexClientReferenceId;

  const query = qs.stringify(parentParams);
  return query ? `${parentUrl}?${query}` : parentUrl;
};
